import React, { useEffect, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import DemographicChart from './DemographicChart';
import ComparisonSelector from '../../../reports/ComparisonSelector';
import { DemographicData } from '../../../types/reports';
import { RequestOptions } from '../../../types/httpRequests';
import useBackendApi from '../../../hooks/useBackendApi';
import useSnackBarAlertMessages from '../../../hooks/useSnackBarAlertMessages';
import GradientCircularProgress from '../../common/GradientCircularProgress';
import { datasetFilterConfig, DatasetKey } from '../../../configs/datasetConfig';
import DemographicBarChart from './DemographicBarChart';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

type DemographicChartContainerProps = {
  nasemScore: number;
  hasAboveRange?: boolean;
};

const backendURL = process.env.REACT_APP_BACKEND_URL;

const DemographicChartContainer: React.FC<DemographicChartContainerProps> = ({ nasemScore, hasAboveRange }) => {
  const [selectedDataset, setSelectedDataset] = useState<DatasetKey>('NHANES');
  const [selectedFilter, setSelectedFilter] = useState<string>(datasetFilterConfig['NHANES'].defaultFilter);
  const [reportDemographicData, setReportDemographicData] = useState<DemographicData | undefined>();
  const [loading, setLoading] = useState<boolean>(true);
  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();

  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { apiFetchRequest } = useBackendApi();

  const handleDatasetChange = (dataset: DatasetKey, filter: string) => {
    setSelectedDataset(dataset);
    setSelectedFilter(filter);
  };

  useEffect(() => {
    const handlefetchNhanesDemographicData = async () => {
      const endpointUrl: string = `${backendURL}/nhanes/demographic-data`;
      const apiRequestOptions: RequestOptions = {
        endpoint: endpointUrl,
      };

      try {
        const demographicDataResponse = await apiFetchRequest(apiRequestOptions);
        if (demographicDataResponse.status !== 200) {
          addAlertMessages({ message: 'Error fetching demographic data', severityType: 'error' });
          return;
        }
        setReportDemographicData(demographicDataResponse.body);
      } catch (error) {
        console.log('error fetching demographic data', error);
        addAlertMessages({ message: 'Error fetching demographic data', severityType: 'error' });
      } finally {
        setLoading(false);
      }
    };
    handlefetchNhanesDemographicData();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <GradientCircularProgress loadingMessage="Loading Graph..." />
      </Box>
    );
  }

  return (
    <>
      {renderSnackBarAlertComponent()}
      <Box
        sx={{
          marginTop: '12px',
          borderRadius: '16px',
          background: theme.custom.report.comparison.container.backgroundColor,
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Comparison Selector */}
        <Box sx={{ width: '100%', marginBottom: '16px' }}>
          <ComparisonSelector onDatasetChange={handleDatasetChange} />
        </Box>

        {/* For large screens, show the line chart and for small screens show the bar graph */}
        <Box sx={{ width: '100%' }}>
          {isMobile ? (
            <DemographicBarChart
              reportDemographicData={reportDemographicData}
              nasemScore={nasemScore}
              selectedFilter={selectedFilter}
              dataset={selectedDataset}
              hasAboveRange={hasAboveRange}
            />
          ) : (
            <DemographicChart
              reportDemographicData={reportDemographicData}
              nasemScore={nasemScore}
              selectedFilter={selectedFilter}
              dataset={selectedDataset}
              hasAboveRange={hasAboveRange}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default DemographicChartContainer;
